import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  updateCurrentUserSettings,
  useCurrentUserSettings,
} from "services/userSettingsService";
import {
  cloneDefaultGridColumns,
  createGridColumnSettingEntry,
  createGridColumnSettingEntries,
  loadGridColumnSettings,
} from "./gridColumnSettings";

const useGridColumnSettings = ({
  gridKey,
  defaultColumns,
  saveDelay = 400,
}) => {
  const { data: userSettings, fetched: userSettingsFetched } =
    useCurrentUserSettings();
  const defaults = useMemo(
    () => cloneDefaultGridColumns(defaultColumns),
    [defaultColumns],
  );
  const [columns, setColumns] = useState(defaults);
  const [resetVersion, setResetVersion] = useState(0);
  const [chooserOpen, setChooserOpen] = useState(false);
  const hydratedRef = useRef(false);
  const columnWidthsRef = useRef({});
  const saveTimeoutRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());
  const pendingEntriesRef = useRef(new Map());

  useEffect(() => {
    if (!userSettingsFetched || hydratedRef.current) return;
    const savedColumns = loadGridColumnSettings({
      gridKey,
      defaultColumns,
      userSettings,
    });
    columnWidthsRef.current = Object.fromEntries(
      savedColumns
        .filter((column) => Number.isFinite(column.width))
        .map((column) => [column.field, column.width]),
    );
    setColumns(savedColumns);
    hydratedRef.current = true;
  }, [defaultColumns, gridKey, userSettings, userSettingsFetched]);

  useEffect(
    () => () => {
      clearTimeout(saveTimeoutRef.current);
    },
    [],
  );

  const writeEntries = useCallback(async (entries) => {
    await updateCurrentUserSettings(entries);
  }, []);

  const scheduleSave = useCallback(
    (entries) => {
      if (!hydratedRef.current) return;
      entries.forEach((entry) =>
        pendingEntriesRef.current.set(entry.key, entry),
      );
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        const pendingEntries = [...pendingEntriesRef.current.values()];
        pendingEntriesRef.current.clear();
        saveQueueRef.current = saveQueueRef.current.then(() =>
          writeEntries(pendingEntries),
        );
      }, saveDelay);
    },
    [saveDelay, writeEntries],
  );

  const toggleColumn = useCallback(
    (field) => {
      setColumns((currentColumns) => {
        const target = currentColumns.find((column) => column.field === field);
        if (!target || target.hideable === false) return currentColumns;
        const visibleCount = currentColumns.filter(
          (column) => column.hideable !== false && column.visible !== false,
        ).length;
        if (target.visible !== false && visibleCount <= 1) return currentColumns;

        const nextColumns = currentColumns.map((column) => ({
          ...column,
          ...(columnWidthsRef.current[column.field] != null
            ? { width: columnWidthsRef.current[column.field] }
            : {}),
          ...(column.field === field
            ? { visible: column.visible === false }
            : {}),
        }));
        const nextColumn = nextColumns.find((column) => column.field === field);
        scheduleSave([
          createGridColumnSettingEntry(
            gridKey,
            field,
            "visible",
            nextColumn.visible !== false,
          ),
        ]);
        return nextColumns;
      });
    },
    [gridKey, scheduleSave],
  );

  const resetColumns = useCallback(() => {
    clearTimeout(saveTimeoutRef.current);
    pendingEntriesRef.current.clear();
    columnWidthsRef.current = {};
    const nextColumns = cloneDefaultGridColumns(defaultColumns);
    setColumns(nextColumns);
    setResetVersion((current) => current + 1);
    if (hydratedRef.current) {
      saveQueueRef.current = saveQueueRef.current.then(() =>
        writeEntries(createGridColumnSettingEntries(gridKey, nextColumns)),
      );
    }
  }, [defaultColumns, gridKey, writeEntries]);

  const handleColumnResize = useCallback(
    (event) => {
      if (!event.end) return;
      const resizedColumns = event.columns ?? [];
      const resizedColumn =
        resizedColumns.find((column) => column.id === event.targetColumnId) ??
        resizedColumns[event.index];
      const field = resizedColumn?.field ?? null;
      const width = Math.round(Number(event.newWidth));
      if (!field || !Number.isFinite(width) || width < 1) return;

      columnWidthsRef.current[field] = width;
      scheduleSave([
        createGridColumnSettingEntry(gridKey, field, "width", width),
      ]);
    },
    [gridKey, scheduleSave],
  );

  const handleColumnReorder = useCallback(
    (event) => {
      setColumns((currentColumns) => {
        const knownFields = new Set(
          currentColumns.map((column) => column.field),
        );
        const orderedFields = [...(event.columns ?? [])]
          .filter((column) => column.field && knownFields.has(column.field))
          .sort((left, right) => left.orderIndex - right.orderIndex)
          .map((column) => column.field);

        if (!orderedFields.length) return currentColumns;

        const orderedSet = new Set(orderedFields);
        const reorderedVisibleColumns = orderedFields
          .map((field) =>
            currentColumns.find((column) => column.field === field),
          )
          .filter(Boolean);
        const hiddenColumns = currentColumns.filter(
          (column) => !orderedSet.has(column.field),
        );
        const nextColumns = [
          ...reorderedVisibleColumns,
          ...hiddenColumns,
        ].map((column, index) => ({
          ...column,
          order: index,
          ...(columnWidthsRef.current[column.field] != null
            ? { width: columnWidthsRef.current[column.field] }
            : {}),
        }));

        scheduleSave(
          nextColumns.map((column) =>
            createGridColumnSettingEntry(
              gridKey,
              column.field,
              "order",
              column.order,
            ),
          ),
        );
        return nextColumns;
      });
    },
    [gridKey, scheduleSave],
  );

  const visibleColumns = useMemo(
    () => columns.filter((column) => column.visible !== false),
    [columns],
  );
  const renderKey = `${columns
    .map((column) => column.field)
    .join("_")}_${resetVersion}`;

  const gridProps = useMemo(
    () => ({
      resizable: true,
      reorderable: true,
      onColumnResize: handleColumnResize,
      onColumnReorder: handleColumnReorder,
    }),
    [handleColumnResize, handleColumnReorder],
  );

  const chooserProps = useMemo(
    () => ({
      columns,
      open: chooserOpen,
      onOpenChange: setChooserOpen,
      onToggle: toggleColumn,
      onReset: resetColumns,
    }),
    [columns, chooserOpen, toggleColumn, resetColumns],
  );

  return {
    columns,
    visibleColumns,
    renderKey,
    toggleColumn,
    resetColumns,
    gridProps,
    chooserProps,
  };
};

export default useGridColumnSettings;
